/**
 * api.js — 数据访问层入口
 *
 * 本项目采用「双模」设计：
 *  - 在线模式（默认）：调用后端 REST 接口（配合 server/ 使用，具备服务端定时提醒能力）
 *  - 演示模式（REACT_APP_DEMO=1）：切换到 localApi，数据全部存于浏览器 localStorage
 *
 * 两种模式的 API 名称与返回结构完全一致，页面组件无需感知差异。
 */
import { localApi } from './localApi';

const DEMO_MODE = process.env.REACT_APP_DEMO === '1';
const BASE_URL = process.env.REACT_APP_API_URL || '';

function getToken() {
  return localStorage.getItem('token') || '';
}

async function request(path, options = {}) {
  const url = `${BASE_URL}${path}`;
  const token = getToken();
  const config = {
    headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
    ...options,
  };
  if (config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body);
  }
  const res = await fetch(url, config);
  if (res.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (!path.startsWith('/api/auth/')) {
      window.location.href = '/login';
    }
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: '网络请求失败' }));
    throw new Error(err.error || `请求失败 (${res.status})`);
  }
  return res.json();
}

const restApi = {
  // 认证
  register: (data) => request('/api/auth/register', { method: 'POST', body: data }),
  login: (data) => request('/api/auth/login', { method: 'POST', body: data }),
  logout: () => request('/api/auth/logout', { method: 'POST' }),
  getMe: () => request('/api/auth/me'),
  changePassword: (data) => request('/api/auth/change-password', { method: 'POST', body: data }),

  // 药品
  getMedicines: () => request('/api/medicines'),
  getMedicine: (id) => request(`/api/medicines/${id}`),
  createMedicine: (data) => request('/api/medicines', { method: 'POST', body: data }),
  updateMedicine: (id, data) => request(`/api/medicines/${id}`, { method: 'PUT', body: data }),
  deleteMedicine: (id) => request(`/api/medicines/${id}`, { method: 'DELETE' }),
  updateMedicineStatus: (id, status) => request(`/api/medicines/${id}/status`, { method: 'PATCH', body: { status } }),
  clearAllData: () => request('/api/medicines/all/clear', { method: 'DELETE' }),

  // 提醒
  getReminders: () => request('/api/reminders'),
  getTodayReminders: () => request('/api/reminders/today'),
  checkReminders: () => request('/api/reminders/check'),
  confirmReminder: (id, data) => request(`/api/reminders/${id}/confirm`, { method: 'POST', body: data }),
  // 弹窗真的显示出来之后回写，服务端才知道提醒送到了设备上
  markReminderDelivered: (id) => request(`/api/reminders/${id}/delivered`, { method: 'POST' }),
  getReminderLogs: (params = '') => request(`/api/reminders/logs${params}`),
  getReminderLogDays: () => request('/api/reminders/logs/days'),

  // 记录
  getRecords: (params = '') => request(`/api/records${params}`),
  getRecordStats: (params = '') => request(`/api/records/stats${params}`),
  getRecordCalendar: (year, month) => request(`/api/records/calendar?year=${year}&month=${month}`),

  // AI
  validateMedicine: (data) => request('/api/ai/validate', { method: 'POST', body: data }),
  ocrMedicine: (imageData) => request('/api/ai/ocr', { method: 'POST', body: { image: imageData } }),
  parseVoice: (text) => request('/api/ai/voice-parse', { method: 'POST', body: { text } }),

  // 设置
  getSettings: () => request('/api/settings'),
  updateSettings: (data) => request('/api/settings', { method: 'PUT', body: data }),

  // 打卡徽章
  getStreak: () => request('/api/streak'),
  getBadges: () => request('/api/badges'),
};

export const api = DEMO_MODE ? localApi : restApi;
export { DEMO_MODE };
